import { colorsPalettes } from "@similarweb/styles";

import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { createColumnFactoryList } from "app/pages/sales-intelligence/common-components/search-results-table/utils/columns";
import type { TrendItem } from "app/pages/sales-intelligence/common-components/table-cells";
import {
    CellDefaultTooltip,
    createLocationCodesCellValue,
    createPrimitiveListCellValue,
    isUpgradeCellValuePresent,
    ListCell,
    LocationCell,
    LocationGroupCell,
    ProgressBarCell,
    renderAbbrNumberWithThresholdCellFactory,
    renderDefaultTextCell,
    renderIndustryCellFactory,
    renderTaxonomyPathDisplayNamesCellFactory,
    renderPercentageBarCellFactory,
    renderPercentageCellFactory,
    renderPercentageStaticCellFactory,
    renderProgressBarCellFactory,
    renderRankCellFactory,
    renderRatingCellFactory,
    renderRoundedNumberCellFactory,
    renderSiteTypeCellFactory,
    renderSparklineCellFactory,
    renderTimeCellFactory,
    renderTrafficChangeFactory,
    SocialLinkCell,
} from "app/pages/sales-intelligence/common-components/table-cells";
import { ItemsCountCell } from "app/pages/sales-intelligence/common-components/table-cells/components/ItemsCountCell/ItemsCountCell";
import TechnologiesCell from "app/pages/sales-intelligence/common-components/table-cells/components/TechnologiesCell/TechnologiesCell";
import {
    renderPercentageCellWithMaxCapFactory,
    renderPercentageCellWithThresholdFactory,
} from "app/pages/sales-intelligence/common-components/table-cells/factories";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import CompanyAppsCell from "app/pages/sales-intelligence/modules/apps-search/components/cells/CompanyAppsCell";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import type { LeadPriorityValue } from "app/pages/sales-intelligence/modules/lists/types/columns";
import EcommerceSignalDataCell from "app/pages/sales-intelligence/sub-modules/signals/components/cells/EcommerceSignalDataCell/EcommerceSignalDataCell";
import {
    COLUMN_AD_NETWORKS_ADDED_NAME,
    COLUMN_AD_NETWORKS_REMOVED_NAME,
    COLUMN_ECOMMERCE_SIGNAL_DATA,
    COLUMN_INTENT_SIGNAL_NAME,
    COLUMN_NEWS_NAME,
    COLUMN_SIGNALS_NAME,
    COLUMN_TECHNOLOGIES_ADDED_NAME,
    COLUMN_TECHNOLOGIES_REMOVED_NAME,
    COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL,
    COLUMN_TRAFFIC_COUNTRIES_NAME,
} from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { SignalAdNetworkCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalAdNetworkCellContainer";
import { SignalCellRightBarLogicContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalCellRightBarLogicContainer";
import { SignalIntentCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalIntentCellContainer/SignalIntentCellContainer";
import { SignalNewsCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalNewsCellContainer/SignalNewsCellContainer";
import { SignalTechnologyCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalTechnologyCellContainer";
import { SignalTrafficCountriesCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalTrafficCountriesCellContainer";
import { SignalTrafficTractionCellContainer } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalTrafficTractionCellContainer";
import type TrafficCountrySignalRowDto from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficCountrySignalRowDto";
import type { NormalizedEcommerceSignalColumnValue } from "app/pages/sales-intelligence/sub-modules/signals/types/columns";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { getSignalsCountFromRow } from "app/pages/sales-intelligence/sub-modules/signals/utils/tableColumns";

import { LeadPriorityCell } from "../../components/cells/LeadPriorityCell/LeadPriorityCell";
import { AD_SPEND_MIN_PERCENT_THRESHOLD, CONVERSION_RATE_CHANGE_MAX_CAP_PERCENT } from "../../constants/misc";
import { CompanyDomainsCellContainer } from "../../containers/search-results-table/CompanyDomainsCell";
import { CrmRecordsCellContainer } from "../../containers/search-results-table/CrmRecordsCell";
import LeadCellContainer from "../../containers/search-results-table/LeadCell/LeadCellContainer";
import { SiUpgradeCellContainer } from "../../containers/SiUpgradeCellContainer/SiUpgradeCellContainer";
import { tableColumnAdapters } from "../../services/column-adapters/tableColumnAdapters";
import type { EnrichedLeadsSearchRow, LeadsSearchColumnListConfig, LeadsSearchRow } from "../../types";
import { columnKeys } from "app/pages/sales-intelligence/utils/i18n/columns";

import { IndustryTaxonomyColumnHeader } from "./components/IndustryTaxonomyColumnHeader";
import { LegacyIndustryColumnHeader } from "./components/LegacyIndustryColumnHeader";
import {
    COLUMN_TRAFFIC_TRACTION_WIDTH,
    COLUMN_TRAFFIC_TREND_WIDTH,
    SIGNALS_COLUMN_WIDTH,
    SIGNALS_TEXT_COLUMN_WIDTH,
} from "./const";
import { createParentCompanyLeadCellRecord, formatTechnologiesArray } from "./utils";

const renderAbbrNumberWithThresholdCell = renderAbbrNumberWithThresholdCellFactory({ min: MIN_RELIABLE_METRIC_VALUE });

const renderPercentageCellWithThreshold = renderPercentageCellWithThresholdFactory(AD_SPEND_MIN_PERCENT_THRESHOLD);

const renderPercentageCellWithMaxCap = renderPercentageCellWithMaxCapFactory(CONVERSION_RATE_CHANGE_MAX_CAP_PERCENT);

export const createBaseColumnList = (): LeadsSearchColumnListConfig =>
    createColumnFactoryList([
        [
            "tech",
            tableColumnAdapters.createDynamicTableColumnAdapter({
                width: 130,
                render: (value: string) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatTechnologiesArray}
                    />
                ),
            }),
        ],
        [
            "techCategory",
            tableColumnAdapters.createDynamicTableColumnAdapter({
                width: 130,
                render: (value: string) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatTechnologiesArray}
                    />
                ),
            }),
        ],
        [
            "techSubCategory",
            tableColumnAdapters.createDynamicTableColumnAdapter({
                width: 130,
                render: (value: string) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatTechnologiesArray}
                    />
                ),
            }),
        ],
        [
            "site",
            tableColumnAdapters.createTableColumnAdapter({
                width: 260,
                fixed: true,
                dataIndex: "site",
                get title() {
                    return translate("grow.lead_generator.exist.table.column.website");
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.tooltip.website");
                },
                render: (value: LeadsSearchRow["site"], record: EnrichedLeadsSearchRow) => (
                    <SiUpgradeCellContainer
                        isUpgradeVisible={isUpgradeCellValuePresent(value)}
                        featureKey={HooksModalV5Key.SalesSolutionDefault}
                    >
                        <LeadCellContainer
                            leadsType="websites"
                            record={record}
                        >
                            {value}
                        </LeadCellContainer>
                    </SiUpgradeCellContainer>
                ),
            }),
        ],
        [
            COLUMN_INTENT_SIGNAL_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_COLUMN_WIDTH,
                fixed: true,
                dataIndex: COLUMN_INTENT_SIGNAL_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return <SignalIntentCellContainer record={record} />;
                },
            }),
        ],
        [
            COLUMN_NEWS_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_COLUMN_WIDTH,
                fixed: true,
                dataIndex: COLUMN_NEWS_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return <SignalNewsCellContainer record={record} />;
                },
            }),
        ],
        [
            COLUMN_TECHNOLOGIES_ADDED_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_TEXT_COLUMN_WIDTH,
                fixed: true,
                get title() {
                    return translate("grow.lead_generator.exist.table.column.addedTechnologies");
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.column.addedTechnologies");
                },
                dataIndex: COLUMN_TECHNOLOGIES_ADDED_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalTechnologyCellContainer
                            record={record}
                            field={COLUMN_TECHNOLOGIES_ADDED_NAME}
                        />
                    );
                },
            }),
        ],
        [
            COLUMN_TECHNOLOGIES_REMOVED_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_TEXT_COLUMN_WIDTH,
                fixed: true,
                get title() {
                    return translate("grow.lead_generator.exist.table.column.removedTechnologies");
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.column.removedTechnologies");
                },
                dataIndex: COLUMN_TECHNOLOGIES_REMOVED_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalTechnologyCellContainer
                            record={record}
                            field={COLUMN_TECHNOLOGIES_REMOVED_NAME}
                        />
                    );
                },
            }),
        ],
        [
            COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_TEXT_COLUMN_WIDTH,
                fixed: true,
                get title() {
                    return translate("grow.lead_generator.exist.table.column.technologiesWithPotentialRenewal");
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.tooltip.technologiesWithPotentialRenewal");
                },
                dataIndex: COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalTechnologyCellContainer
                            record={record}
                            field={COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL}
                        />
                    );
                },
            }),
        ],
        [
            COLUMN_AD_NETWORKS_ADDED_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_TEXT_COLUMN_WIDTH,
                fixed: true,
                dataIndex: COLUMN_AD_NETWORKS_ADDED_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalAdNetworkCellContainer
                            record={record}
                            field={COLUMN_AD_NETWORKS_ADDED_NAME}
                        />
                    );
                },
            }),
        ],
        [
            COLUMN_AD_NETWORKS_REMOVED_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_TEXT_COLUMN_WIDTH,
                fixed: true,
                dataIndex: COLUMN_AD_NETWORKS_REMOVED_NAME,
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.tooltip.removedAdNetworks");
                },
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalAdNetworkCellContainer
                            record={record}
                            field={COLUMN_AD_NETWORKS_REMOVED_NAME}
                        />
                    );
                },
            }),
        ],
        [
            COLUMN_TRAFFIC_COUNTRIES_NAME,
            tableColumnAdapters.createSignalTableColumnAdapter({
                width: COLUMN_TRAFFIC_TRACTION_WIDTH,
                dataIndex: COLUMN_TRAFFIC_COUNTRIES_NAME,
                render: (value: TrafficCountrySignalRowDto[], record: EnrichedLeadsSearchRow, index: number) => {
                    return (
                        <SignalTrafficCountriesCellContainer
                            value={value}
                            record={record}
                            index={index}
                        />
                    );
                },
            }),
        ],
        [
            "traffic_six_months_visits",
            tableColumnAdapters.createSignalTableColumnAdapter({
                width: COLUMN_TRAFFIC_TREND_WIDTH,
                dataIndex: "traffic_six_months_visits",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "sixMonthsTrendDirect",
            tableColumnAdapters.createSignalTableColumnAdapter({
                width: COLUMN_TRAFFIC_TRACTION_WIDTH,
                dataIndex: "sixMonthsTrendDirect",
                render: (value: TrendItem[]) => <SignalTrafficTractionCellContainer value={value} />,
            }),
        ],

        [
            "traffic_change",
            tableColumnAdapters.createSignalTableColumnAdapter({
                width: COLUMN_TRAFFIC_TREND_WIDTH,
                dataIndex: "traffic_change",
                render: (value: number) => renderTrafficChangeFactory(value),
            }),
        ],
        [
            COLUMN_ECOMMERCE_SIGNAL_DATA,
            tableColumnAdapters.createSignalTableColumnAdapter({
                width: COLUMN_TRAFFIC_TREND_WIDTH,
                align: "end",
                dataIndex: COLUMN_ECOMMERCE_SIGNAL_DATA,
                columnPickerTitle: translate(signalsI18nKeys.ecommerce.tableColumnEcommerceTrend),
                render: (value: NormalizedEcommerceSignalColumnValue) => <EcommerceSignalDataCell value={value} />,
            }),
        ],
        [
            "country",
            tableColumnAdapters.createTableColumnAdapter({
                width: 152,
                dataIndex: "country",
                get title() {
                    return translate("grow.lead_generator.exist.table.column.visits_from");
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.tooltip.country");
                },
                render: (value: string, _record: EnrichedLeadsSearchRow, index: number) => {
                    const locationCodes = createLocationCodesCellValue(value);

                    return (
                        <LocationGroupCell
                            locationCodes={locationCodes}
                            recordIndex={index}
                        />
                    );
                },
            }),
        ],
        [
            "top_geo_country",
            tableColumnAdapters.createTableColumnAdapter({
                width: 180,
                dataIndex: "top_geo_country",
                render: (value: LeadsSearchRow["top_geo_country"]) => {
                    return <LocationCell locationCode={value} />;
                },
                get tooltipTitle() {
                    return translate("workspaces.totalvisits.table.column.top_geo_country.tooltip");
                },
            }),
        ],
        [
            "rank",
            tableColumnAdapters.createTableColumnAdapter({
                width: 85,
                dataIndex: "rank",
                align: "end",
                render: (value: number) => renderRankCellFactory(value),
            }),
        ],
        [
            "visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "desktop_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "mobileweb_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "desktop_visitors",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_visitors",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "mobileweb_visitors",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_visitors",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "visitors",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "visitors",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "avg_monthly_estimated_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "avg_monthly_estimated_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "monthly_visits_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "monthly_visits_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "monthly_visits_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "monthly_visits_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_monthly_visits_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_monthly_visits_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_monthly_visits_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_monthly_visits_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_monthly_visits_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "mobile_monthly_visits_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_monthly_visits_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "mobile_monthly_visits_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_unique_monthly_visitors_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_unique_monthly_visitors_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_unique_monthly_visitors_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_unique_monthly_visitors_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_unique_monthly_visitors_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "mobile_unique_monthly_visitors_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_unique_monthly_visitors_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "mobile_unique_monthly_visitors_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_total_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_total_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "last_months_total_pageviews",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_total_pageviews",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "last_months_total_visitors",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_total_visitors",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "desktop_avg_visit_duration",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_avg_visit_duration",
                align: "end",
                render: renderTimeCellFactory,
            }),
        ],
        [
            "mobileweb_avg_visit_duration",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_avg_visit_duration",
                align: "end",
                render: renderTimeCellFactory,
            }),
        ],
        [
            "avg_visit_duration",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "avg_visit_duration",
                align: "end",
                render: renderTimeCellFactory,
            }),
        ],
        [
            "avg_visits_per_user",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "avg_visits_per_user",
                align: "end",
                render: renderRoundedNumberCellFactory,
            }),
        ],
        [
            "desktop_pages_per_visit",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_pages_per_visit",
                align: "end",
                render: renderRoundedNumberCellFactory,
            }),
        ],
        [
            "mobileweb_pages_per_visit",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_pages_per_visit",
                align: "end",
                render: renderRoundedNumberCellFactory,
            }),
        ],
        [
            "pages_per_visit",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "pages_per_visit",
                align: "end",
                render: renderRoundedNumberCellFactory,
            }),
        ],
        [
            "desktop_pageviews",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_pageviews",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "mobileweb_pageviews",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_pageviews",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "pageviews",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "pageviews",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "bounce_rate",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "bounce_rate",
                align: "end",
                render: renderPercentageStaticCellFactory,
            }),
        ],
        [
            "desktop_bounce_rate",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "desktop_bounce_rate",
                align: "end",
                render: renderPercentageStaticCellFactory,
            }),
        ],
        [
            "mobileweb_bounce_rate",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "mobileweb_bounce_rate",
                align: "end",
                render: renderPercentageStaticCellFactory,
            }),
        ],
        [
            "conversion_rate",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "conversion_rate",
                align: "end",
                render: renderPercentageStaticCellFactory,
            }),
        ],
        [
            "conversion_rate_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "conversion_rate_change_mom",
                align: "end",
                render: renderPercentageCellWithMaxCap,
            }),
        ],
        [
            "conversion_rate_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "conversion_rate_change_yoy",
                align: "end",
                render: renderPercentageCellWithMaxCap,
            }),
        ],
        [
            "direct_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "direct_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "mail_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "mail_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "referrals_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "referrals_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "social_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "social_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "direct_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "direct_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "mail_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "mail_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "referrals_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "referrals_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "social_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "social_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "direct_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "direct_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "direct_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "direct_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_direct_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_direct_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "mail_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "mail_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mail_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "mail_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_mail_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_mail_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "referrals_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "referrals_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "referrals_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "referrals_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_referrals_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_referrals_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "social_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "social_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "social_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "social_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_social_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_social_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "organic_social_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "organic_social_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "organic_social_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "organic_social_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "organic_social_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "organic_social_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "organic_social_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "organic_social_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_organic_social_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_organic_social_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "paid_social_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "paid_social_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "paid_social_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "paid_social_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "paid_social_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "paid_social_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "paid_social_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "paid_social_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_paid_social_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_paid_social_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "gen_ai_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "gen_ai_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "gen_ai_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "gen_ai_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "gen_ai_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "gen_ai_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "gen_ai_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "gen_ai_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_gen_ai_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_gen_ai_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "affiliate_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "affiliate_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "affiliate_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "affiliate_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "affiliate_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "affiliate_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "affiliate_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "affiliate_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_affiliate_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_affiliate_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "search_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "search_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "search_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "search_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "search_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "search_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "search_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "search_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_search_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_search_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "organic_search_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 150,
                dataIndex: "organic_search_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "organic_search_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "organic_search_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "organic_search_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "organic_search_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "organic_search_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "organic_search_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_vs_mobileweb_visits_share",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 190,
                dataIndex: "desktop_vs_mobileweb_visits_share",
                render: (value: LeadsSearchRow["desktop_vs_mobileweb_visits_share"]) =>
                    renderPercentageBarCellFactory(value),
            }),
        ],
        [
            "last_months_organic_search_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_organic_search_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "paid_search_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "paid_search_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "paid_search_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "paid_search_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "paid_search_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "paid_search_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "paid_search_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "paid_search_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_paid_search_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_paid_search_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "usd_spend",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "usd_spend",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
                tooltipTitle: "si.lead_gen_filters.group.usdSpend.tooltip",
            }),
        ],
        [
            "displayads_visits",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "displayads_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "displayads_visits_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 130,
                dataIndex: "displayads_visits_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "displayads_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "displayads_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "displayads_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "displayads_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "last_months_displayads_visits",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 140,
                dataIndex: "last_months_displayads_visits",
                align: "end",
                render: (value: TrendItem[]) => renderSparklineCellFactory(value),
            }),
        ],
        [
            "search_channel_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "search_channel_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_channel_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "display_channel_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "total_ad_spend",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ad_spend",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "total_ad_spend_change_mom",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ad_spend_change_mom",
                align: "end",
                render: renderPercentageCellWithThreshold,
            }),
        ],
        [
            "search_ad_spend",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ad_spend",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "search_ad_spend_change_mom",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ad_spend_change_mom",
                align: "end",
                render: renderPercentageCellWithThreshold,
            }),
        ],
        [
            "display_ad_spend",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ad_spend",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ad_spend_change_mom",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ad_spend_change_mom",
                align: "end",
                render: renderPercentageCellWithThreshold,
            }),
        ],
        [
            "total_ad_active_campaigns",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ad_active_campaigns",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "search_ad_active_campaigns",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ad_active_campaigns",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ad_active_campaigns",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ad_active_campaigns",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "total_ad_number",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ad_number",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "search_ad_number",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ad_number",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ad_number",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ad_number",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "total_ads_desktop_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ads_desktop_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "total_ads_mobile_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "total_ads_mobile_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "search_ads_desktop_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ads_desktop_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "search_ads_mobile_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "search_ads_mobile_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ads_desktop_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ads_desktop_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ads_mobile_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ads_mobile_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ads_banner_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ads_banner_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ads_video_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ads_video_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "display_ads_native_share",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "display_ads_native_share",
                align: "end",
                render: (v: string) => renderDefaultTextCell(v),
            }),
        ],
        [
            "company_name",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_name",
            }),
        ],
        [
            "company_revenue_range",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_revenue_range",
                fixed: false,
                align: "end",
            }),
        ],
        [
            "industry",
            tableColumnAdapters.createTableColumnAdapter({
                width: 185,
                dataIndex: "industry",
                title: <LegacyIndustryColumnHeader />,
                get columnPickerTitle() {
                    return translate(columnKeys.title("industry"));
                },
                get tooltipTitle() {
                    return translate("grow.lead_generator.exist.table.tooltip.site_category");
                },
            }),
        ],
        [
            "industry_taxonomy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 260,
                dataIndex: "industry_taxonomy",
                render: renderTaxonomyPathDisplayNamesCellFactory,
                // The cell truncates and tooltips itself; rc-table would otherwise also stamp a native
                // `title`, showing two tooltips on hover.
                ellipsis: { showTitle: false },
                title: <IndustryTaxonomyColumnHeader />,
                get columnPickerTitle() {
                    return translate(leadsSearchFiltersI18nKeys.industryTaxonomyName());
                },
            }),
        ],
        [
            "unique_monthly_visitors_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "unique_monthly_visitors_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "unique_monthly_visitors_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "unique_monthly_visitors_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "total_page_views_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "total_page_views_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "total_page_views_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "total_page_views_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_total_page_views_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "desktop_total_page_views_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "desktop_total_page_views_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "desktop_total_page_views_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_total_page_views_change_mom",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "mobile_total_page_views_change_mom",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "mobile_total_page_views_change_yoy",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "mobile_total_page_views_change_yoy",
                align: "end",
                render: renderPercentageCellFactory,
            }),
        ],
        [
            "male_vs_female_share",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 190,
                dataIndex: "male_vs_female_share",
                render: (value: LeadsSearchRow["male_vs_female_share"]) => renderPercentageBarCellFactory(value),
            }),
        ],
        [
            "age_group_18_24_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_18_24_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "age_group_25_34_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_25_34_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "age_group_35_44_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_35_44_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "age_group_45_54_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_45_54_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "age_group_55_64_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_55_64_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "age_group_65_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "age_group_65_share",
                align: "end",
                render: renderProgressBarCellFactory,
            }),
        ],
        [
            "company_description",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                dataIndex: "company_description",
            }),
        ],
        [
            "company_founding_year",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_founding_year",
            }),
        ],
        [
            "total_funding_amount",
            tableColumnAdapters.createTableColumnAdapter({
                width: 118,
                dataIndex: "total_funding_amount",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "company_online_revenue_range",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_online_revenue_range",
                align: "end",
            }),
        ],
        [
            "monthly_avg_transactions_range",
            tableColumnAdapters.createTableColumnAdapter({
                width: 165,
                dataIndex: "monthly_avg_transactions_range",
                align: "end",
            }),
        ],
        [
            "international_visits",
            tableColumnAdapters.createTableColumnAdapter({
                width: 150,
                dataIndex: "international_visits",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "international_visits_share",
            tableColumnAdapters.createTableColumnAdapter({
                width: 180,
                dataIndex: "international_visits_share",
                render: (value: number) => {
                    return (
                        <ProgressBarCell
                            value={value || 0}
                            ChangeValueProps={{ unsigned: true }}
                            primaryColor={colorsPalettes.blue[400]}
                            secondaryColor={colorsPalettes.sky[300]}
                        />
                    );
                },
            }),
        ],
        [
            "company_employee_range",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_employee_range",
                fixed: false,
                align: "end",
            }),
        ],
        [
            "business_model",
            tableColumnAdapters.createTableColumnAdapter({
                width: 132,
                dataIndex: "business_model",
            }),
        ],
        [
            "company_country",
            tableColumnAdapters.createTableColumnAdapter({
                width: 145,
                dataIndex: "company_country",
            }),
        ],
        [
            "company_state",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_state",
            }),
        ],
        [
            "company_city",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_city",
            }),
        ],
        [
            "company_address",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_address",
            }),
        ],
        [
            "company_zip",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_zip",
                align: "end",
            }),
        ],
        [
            "siren_number",
            tableColumnAdapters.createTableColumnAdapter({
                width: 110,
                dataIndex: "siren_number",
                align: "end",
            }),
        ],
        [
            "australian_business_number",
            tableColumnAdapters.createTableColumnAdapter({
                width: 110,
                dataIndex: "australian_business_number",
                align: "end",
            }),
        ],
        [
            "company_phone",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_phone",
                align: "end",
            }),
        ],
        [
            "company_email",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "company_email",
                align: "end",
            }),
        ],
        [
            "naics_codes",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 135,
                dataIndex: "naics_codes",
                render: (value: string[]) => (
                    <CellDefaultTooltip content={value?.join(", ")}>
                        {renderIndustryCellFactory(value)}
                    </CellDefaultTooltip>
                ),
            }),
        ],
        [
            "sic_codes",
            tableColumnAdapters.createTableColumnAdapter({
                sorter: false,
                width: 135,
                dataIndex: "sic_codes",
                render: (value: string[]) => (
                    <CellDefaultTooltip content={value?.join(", ")}>
                        {renderIndustryCellFactory(value)}
                    </CellDefaultTooltip>
                ),
            }),
        ],
        [
            "site_types",
            tableColumnAdapters.createTableColumnAdapter({
                width: 185,
                dataIndex: "site_types",
                render: (value: string[]) => (
                    <CellDefaultTooltip content={value.join(", ")}>
                        {renderSiteTypeCellFactory(value)}
                    </CellDefaultTooltip>
                ),
                tooltipTitle: "grow.lead_generator.exist.table.tooltip.site_types",
            }),
        ],
        [
            "keywords_clicks",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "keywords_clicks",
                align: "end",
                fixed: false,
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "site_tags",
            tableColumnAdapters.createTableColumnAdapter({
                dataIndex: "site_tags",
                render: (value: string[]) => (
                    <ListCell
                        value={value}
                        dataAutomation="site-tags"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "apps_rating",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "apps_rating",
                align: "end",
                render: renderRatingCellFactory(),
            }),
        ],
        [
            "apps_monthly_downloads",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "apps_monthly_downloads",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "apps_monthly_active_users",
            tableColumnAdapters.createDetailedTableColumnAdapter({
                width: 120,
                dataIndex: "apps_monthly_active_users",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "lead_priority",
            tableColumnAdapters.createTableColumnAdapter({
                width: 128,
                dataIndex: "lead_priority",
                render: (value: LeadPriorityValue | null, record: EnrichedLeadsSearchRow) => (
                    <LeadPriorityCell
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
    ]);

export const createLeadsSearchColumnList = (): LeadsSearchColumnListConfig =>
    createColumnFactoryList([
        ...createBaseColumnList(),
        [
            "company",
            tableColumnAdapters.createTableColumnAdapter({
                width: 212,
                dataIndex: "company",
                render: (value: LeadsSearchRow["company"], record: EnrichedLeadsSearchRow) => (
                    <SiUpgradeCellContainer
                        isUpgradeVisible={isUpgradeCellValuePresent(value)}
                        featureKey={HooksModalV5Key.SalesSolutionDefault}
                    >
                        <LeadCellContainer
                            leadsType="companies"
                            record={record}
                        >
                            {value}
                        </LeadCellContainer>
                    </SiUpgradeCellContainer>
                ),
            }),
        ],
        [
            "parent_company",
            tableColumnAdapters.createTableColumnAdapter({
                width: 212,
                dataIndex: "parent_company",
                sorter: false,
                render: (value: LeadsSearchRow["parent_company"], record: EnrichedLeadsSearchRow) => {
                    if (!value?.domain || !value.name) {
                        return renderDefaultTextCell(NO_DATA_SIGN);
                    }

                    const parentCompanyRecord = createParentCompanyLeadCellRecord(value, record);

                    return (
                        <LeadCellContainer
                            leadsType="companies"
                            record={parentCompanyRecord}
                        >
                            {value.name}
                        </LeadCellContainer>
                    );
                },
            }),
        ],
        [
            "company_domains",
            tableColumnAdapters.createTableColumnAdapter({
                width: 212,
                dataIndex: "company_domains",
                sorter: false,
                render: (value: LeadsSearchRow["company_domains"], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SiUpgradeCellContainer
                            isUpgradeVisible={isUpgradeCellValuePresent(record.company)}
                            featureKey={HooksModalV5Key.SalesSolutionDefault}
                        >
                            <CompanyDomainsCellContainer
                                value={value}
                                record={record}
                            />
                        </SiUpgradeCellContainer>
                    );
                },
            }),
        ],
        [
            "company_apps",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "company_apps",
                title: translate("si.leads-search.table.column.company-apps"),
                tooltipTitle: translate("si.leads-search.table.column.company-apps.tooltip"),
                render: (value: LeadsSearchRow["company_apps"]) => <CompanyAppsCell value={value} />,
            }),
        ],
        [
            COLUMN_SIGNALS_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_COLUMN_WIDTH,
                fixed: true,
                dataIndex: COLUMN_SIGNALS_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalCellRightBarLogicContainer row={record}>
                            <ItemsCountCell value={getSignalsCountFromRow(record)} />
                        </SignalCellRightBarLogicContainer>
                    );
                },
            }),
        ],
        [
            "salesforce_accounts_count",
            tableColumnAdapters.createTableColumnAdapter({
                width: 150,
                dataIndex: "salesforce_accounts_count",
                render: (value: number, record: EnrichedLeadsSearchRow) => (
                    <CrmRecordsCellContainer
                        integrationProvider="salesforce"
                        record={record}
                        value={value}
                    />
                ),
            }),
        ],
        [
            "hubspot_accounts_count",
            tableColumnAdapters.createTableColumnAdapter({
                width: 150,
                dataIndex: "hubspot_accounts_count",
                render: (value: number, record: EnrichedLeadsSearchRow) => (
                    <CrmRecordsCellContainer
                        integrationProvider="hubspot"
                        record={record}
                        value={value}
                    />
                ),
            }),
        ],
        [
            "dynamics_accounts_count",
            tableColumnAdapters.createTableColumnAdapter({
                width: 150,
                dataIndex: "dynamics_accounts_count",
                render: (value: number, record: EnrichedLeadsSearchRow) => (
                    <CrmRecordsCellContainer
                        integrationProvider="dynamics"
                        record={record}
                        value={value}
                    />
                ),
            }),
        ],
        [
            "linkedin_url",
            tableColumnAdapters.createTableColumnAdapter({
                width: 95,
                dataIndex: "linkedin_url",
                render: (value: string) => (
                    <SocialLinkCell
                        iconName="linkedin-colored"
                        tooltipText={translate("table.cell.linkedin-url.tooltip.text")}
                        url={value}
                        dataAutomation="social-network-linkedin"
                    />
                ),
            }),
        ],
    ]);
